import type { PostTablesLabels200DataItem } from '@ipfabric/api-client/client/schemas/postTablesLabels200DataItem';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { DiscoveryDataEnrichmentLink } from '../discoveryDataEnrichmentLink';
import {
  readActionSourceLink,
  readTableCell,
  toEditableAssignedLabel,
} from './assignedLabelsRow';
import type { EditableAssignedLabel } from './assignedLabelsTypes';

type AssignedLabelsTableColumnHandlers = {
  onDelete: (row: PostTablesLabels200DataItem) => void;
  onEdit: (assignment: EditableAssignedLabel) => void;
  disabledReason?: string;
  /** Omitted when the user cannot reach the assignment source. */
  onOpenSource?: (link: DiscoveryDataEnrichmentLink) => void;
};

export const createAssignedLabelsTableColumns = ({
  onDelete,
  onEdit,
  disabledReason,
  onOpenSource,
}: AssignedLabelsTableColumnHandlers) => [
  createFrontendOnlyColumn<PostTablesLabels200DataItem>({
    key: 'actions',
    title: 'Actions',
    disabledSort: true,
    webDependencies: [
      'labelId',
      'type',
      'assignment',
      'label',
      'target',
      'hostname',
      'rootAssignment',
      'rootId',
      'rootRuleId',
    ],
    render: (row) => {
      const assignment = toEditableAssignedLabel(row);
      const labelId = readTableCell(row.labelId);
      const source = readActionSourceLink(row);
      const openSourceAction =
        onOpenSource === undefined || source === undefined ? null : (
          <Tooltip content={source.tooltip}>
            <span>
              <TableButton
                dataTestId={
                  labelId === undefined
                    ? undefined
                    : `assigned-label-source-${labelId}`
                }
                icon='fa-arrow-right'
                onClick={() => onOpenSource(source.link)}
                size='default'
                type='button'
                variant='plain'
              />
            </span>
          </Tooltip>
        );

      if (!assignment && openSourceAction === null) {
        return null;
      }

      const disabled = disabledReason !== undefined;

      return (
        <div className='flex flex-row items-center gap-xs'>
          {assignment ? (
            <>
              <Tooltip content={disabledReason}>
                <span>
                  <TableButton
                    dataTestId={`assigned-label-edit-${assignment.labelId}`}
                    disabled={disabled}
                    icon='fa-pen'
                    onClick={() => onEdit(assignment)}
                    size='default'
                    type='button'
                    variant='plain'
                  />
                </span>
              </Tooltip>
              <Tooltip content={disabledReason}>
                <span>
                  <TableButton
                    accent='negative'
                    dataTestId={`assigned-label-delete-${assignment.labelId}`}
                    disabled={disabled}
                    icon='fa-trash-can'
                    onClick={() => onDelete(row)}
                    size='default'
                    type='button'
                    variant='plain'
                  />
                </span>
              </Tooltip>
            </>
          ) : null}
          {openSourceAction}
        </div>
      );
    },
  }),
];
